/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { useEffect } from 'react';
import usePlatformGroupAnalytics from '@cont/BatchGroup/usePlatformGroupAnalytics';
import batchGroup from '@models/BatchGroup';
import {
  getBatchConfig,
  getBusinessPlatform,
  getHref
} from '@cont/BatchGroup/utils';
import {
  cardType,
  MAXIMUM_BATCHES_TO_SHOW_WITHOUT_SEE_ALL
} from '@cont/BatchGroup/constants';
import BenefitsList from '@cont/BatchGroup/BenefitsList';
import {
  PLATFORM_PAGE_LAND,
  SEE_ALL_CLICKED
} from '@cont/BatchGroup/constants/events';
import CardCarousel from './CardCarousel';
import PlatformBatchCard from '../../Composite/BatchCard/PlatFormBatchCard';
import { CaraouselInfo } from './PFTrialClasses';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: ${Spacings.SPACING_30B};
  margin-bottom: 130px;
  width: 1376px;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-top: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_14B};
    margin-bottom: -${Spacings.SPACING_4B};
    width: 100%;
  }
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 1136px;
  margin: auto;
`;

const StyH3 = styled(H3)`
  font-size: ${Fonts.H2};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H4};
  }
`;

const SubLabel = styled(P1)`
  font-size: ${Fonts.H5};
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;
const StyledPlatformBatchCard = styled(PlatformBatchCard)`
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    width: ${({ $hasFullWidth }) => ($hasFullWidth ? '330px' : '292px')};
  }
`;

const SinglePlatformBatchCard = styled(StyledPlatformBatchCard)`
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_0B};
    width: 100%;
  }
`;
const Wrapper = styled.div`
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B} ${Spacings.SPACING_10B}
    ${Spacings.SPACING_4B};
`;

const BenefitsWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const UpcomingSection = ({
  block,
  href,
  as,
  type,
  goalUID,
  goalDetails,
  eventName,
  lpss,
  maximumItemToShowSeeAll,
  cardType: platformCardSize,
  lpssForBatchCard,
  batchType
}) => {
  const { isTablet, isMobile } = useWindowSize();
  const { data } = block;
  const label = block?.extraBlockInfo?.header || 'Upcoming batches';
  const subLabel = block?.extraBlockInfo?.description;
  const benefits = block?.extraBlockInfo?.benefits ?? [];
  const formattedBatchGroupData = data?.map?.((batchGroupData) =>
    batchGroup.getEntityData(batchGroupData)
  );
  const hasBatchGroups = !!formattedBatchGroupData.length;
  const sendAnalyticsToSegment = usePlatformGroupAnalytics(goalUID);
  useEffect(() => {
    sendAnalyticsToSegment(PLATFORM_PAGE_LAND, {
      last_primary_source_section: 'Platform Home',
      current_screen: 'Platform Home',
      business_platform: getBusinessPlatform(goalDetails),
      goal_name: goalDetails.name
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSeeAll = () => {
    sendAnalyticsToSegment(SEE_ALL_CLICKED, {
      last_primary_source_section: lpss
    });
    sendAnalyticsToSegment(eventName, {
      last_primary_source_section: lpss,
      batch_type: batchType,
      is_empty_state: false
    });
  };

  const showSingleBatch = formattedBatchGroupData?.length === 1 && isMobile;
  const { updatedHref, updatedAs } = getHref(
    href,
    as,
    isMobile,
    formattedBatchGroupData,
    maximumItemToShowSeeAll
  );
  return (
    <SecWrapper className="platform-batches-section">
      {showSingleBatch ? (
        <PlatformBatch
          label={label}
          subLabel={subLabel}
          batch={formattedBatchGroupData[0]}
          lpssForBatchCard={lpssForBatchCard}
          cardType={platformCardSize}
          extraBenefits={<BenefitsList items={benefits} />}
        />
      ) : (
        <CardCarousel
          label={label}
          subLabel={subLabel}
          step={hasBatchGroups && !(isTablet || isMobile) ? 3 : 2}
          href={updatedHref}
          as={updatedAs}
          dataAnalytics={type}
          type={type}
          handleSeeAll={handleSeeAll}
          extraBenefits={
            <BenefitsWrapper>
              <BenefitsList items={benefits} />
            </BenefitsWrapper>
          }
        >
          {formattedBatchGroupData?.map((batch, index) => (
            <StyledPlatformBatchCard
              label={label}
              index={index}
              key={batch.uid}
              batch={batch}
              lastPrimarySourceSection={lpssForBatchCard}
              dataAnalytics="batch"
              cardType={platformCardSize}
            />
          ))}
        </CardCarousel>
      )}
    </SecWrapper>
  );
};

export default UpcomingSection;

const PlatformBatch = ({
  label,
  batch,
  subLabel,
  lpssForBatchCard,
  cardType: singlePlatformCardSize,
  extraBenefits
}) => {
  return (
    <>
      <Wrapper>
        {label ? (
          <CaraouselInfo>
            <Column>
              <StyH3>{label}</StyH3>
              {subLabel && <SubLabel>{subLabel}</SubLabel>}
              {extraBenefits}
            </Column>
          </CaraouselInfo>
        ) : null}
        <SinglePlatformBatchCard
          label={label}
          key={batch.uid}
          batch={batch}
          lastPrimarySourceSection={lpssForBatchCard}
          dataAnalytics="batch"
          $hasFullWidth
          cardType={singlePlatformCardSize}
        />
      </Wrapper>
    </>
  );
};

export const UpcomingBatchGroups = (props) => {
  const { type } = props;
  const { eventName, lpss, lpssForBatchCard, batchType } = getBatchConfig(type);
  return (
    <UpcomingSection
      {...props}
      maximumItemToShowSeeAll={MAXIMUM_BATCHES_TO_SHOW_WITHOUT_SEE_ALL}
      cardType={cardType.LARGE}
      eventName={eventName}
      lpss={lpss}
      lpssForBatchCard={lpssForBatchCard}
      batchType={batchType}
    />
  );
};
